import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Clock, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const SessionContext = createContext(null);

// Session timeout: 30 minutes (configurable)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
// Warning: 2 minutes before timeout
const WARNING_BEFORE_MS = 2 * 60 * 1000;

export const SessionProvider = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  const handleSessionTimeout = useCallback(async () => {
    setShowTimeoutWarning(false);
    
    // Perform logout
    try {
      await logout({ force: true });
      toast.info('Session expired. Please log in again.');
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/auth');
    }
  }, [logout, navigate]);

  const handleWarning = useCallback(() => {
    setShowTimeoutWarning(true);
  }, []);

  const {
    showWarning,
    formatRemainingTime,
    extendSession,
  } = useSessionTimeout({
    timeoutMs: SESSION_TIMEOUT_MS,
    warningMs: WARNING_BEFORE_MS,
    onTimeout: handleSessionTimeout,
    onWarning: handleWarning,
    enabled: !!user, // Only enable when user is logged in
  });

  const handleExtendSession = useCallback(() => {
    extendSession();
    setShowTimeoutWarning(false);
    toast.success('Session extended');
  }, [extendSession]);

  const handleLogoutNow = useCallback(async () => {
    setShowTimeoutWarning(false);
    try {
      await logout({ force: true });
      toast.success('Logged out successfully');
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/auth');
    }
  }, [logout, navigate]);

  return (
    <SessionContext.Provider value={{ extendSession }}>
      {children}
      
      {/* Session Timeout Warning Dialog */}
      <AlertDialog open={showTimeoutWarning && !!user} onOpenChange={setShowTimeoutWarning}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <AlertDialogTitle className="text-xl">Session Expiring Soon</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              Your session will expire due to inactivity. You will be logged out in:
              <div className="text-center my-4">
                <span className="text-4xl font-bold text-yellow-600 font-mono">
                  {formatRemainingTime()}
                </span>
              </div>
              Would you like to stay logged in?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-3 sm:gap-3">
            <AlertDialogCancel 
              onClick={handleLogoutNow}
              className="flex-1 gap-2"
            >
              <LogOut className="w-4 h-4" />
              Log Out Now
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleExtendSession}
              className="flex-1 bg-blue-600 hover:bg-blue-500"
            >
              Stay Logged In
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export default SessionProvider;
